import { useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from "react";
import { mediaBox, resizeTarget, type MediaResizeChange, type MediaSize } from "./mediaSize";

const SIZE_LABEL: Record<MediaSize, string> = { small: "Small", medium: "Medium", full: "Full width" };

/**
 * A picture's or video's box, centred in its column. In the editor, on
 * screens with a mouse, a bar on each side resizes it from the middle, like
 * Notion's; it settles on Small, Medium or Full width when close to them.
 */
export function MediaResize({
  size,
  width,
  onResize,
  children,
}: {
  size: MediaSize;
  width?: number;
  /** Only in the editor. */
  onResize?: (change: MediaResizeChange) => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<MediaResizeChange | null>(null);
  const shown = drag ?? (width ? { width } : { size });
  const { className, style } = mediaBox({ size: "size" in shown ? shown.size : size, width: shown.width });
  const start = (event: PointerEvent<HTMLButtonElement>, side: -1 | 1) => {
    const element = box.current;
    const column = element?.parentElement;
    if (!element || !column || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const from = event.clientX;
    const startWidth = element.getBoundingClientRect().width;
    const columnWidth = column.getBoundingClientRect().width;
    let latest: MediaResizeChange | null = null;
    const move = (next: globalThis.PointerEvent) => {
      // Centred, so each side moves by the drag: the width changes twice over.
      const px = startWidth + 2 * side * (next.clientX - from);
      latest = resizeTarget((px / columnWidth) * 100);
      setDrag(latest);
    };
    const end = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", end);
      handle.removeEventListener("pointercancel", end);
      setDrag(null);
      if (latest) onResize?.(latest);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
  };
  const bar = (side: -1 | 1) => (
    <button
      type="button"
      aria-label={side < 0 ? "Resize from the left" : "Resize from the right"}
      tabIndex={-1}
      onPointerDown={(event) => start(event, side)}
      onClick={(event) => event.stopPropagation()}
      className={`media-resize-bar absolute top-1/2 z-2 hidden h-12 max-h-[40%] w-3 -translate-y-1/2 cursor-ew-resize place-items-center opacity-0 transition-opacity group-hover/media:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:grid ${side < 0 ? "left-1.5" : "right-1.5"} ${drag ? "opacity-100" : ""}`}
    >
      <span className="h-full w-1.5 rounded-full border border-hazumu-white/90 bg-hazumu-ink/60 shadow-sm" />
    </button>
  );
  return (
    <div ref={box} className={`group/media relative mx-auto ${className}`} style={style as CSSProperties | undefined}>
      {children}
      {onResize && (
        <>
          {bar(-1)}
          {bar(1)}
          {drag && (
            <span className="pointer-events-none absolute top-2 left-1/2 z-2 -translate-x-1/2 rounded-full bg-hazumu-ink/75 px-2 py-0.5 text-xs text-hazumu-white">
              {"size" in drag ? SIZE_LABEL[drag.size] : `${drag.width}%`}
            </span>
          )}
        </>
      )}
    </div>
  );
}
